// components/Students/StudentForm.js
import React, { useEffect, useState } from "react";
import { User, BookOpen, IndianRupee, ClipboardList, Plus, Trash2, Save, GraduationCap, Building2 } from "lucide-react";
import {
  AppCard, AppCardContent, AppCardHeader, AppCardTitle,
  AppButton, BackButton,
} from "../ui";
import CommonToaster from "../Common/CommonToaster";
import apiClient from "../../utils/apiClient";
import { useNavigate } from "react-router-dom";
import { useBranch } from "../../contexts/BranchContext";
import "./Students.scss";

const EMPTY_ADDITIONAL = [];

const initialForm = {
  name: '',
  email: '',
  mobile: '',
  dob: '',
  gender: '',
  address: '',
  course: '',
  batch: '',
  degree: '',
  passout: '',
  experience: '',
  totalFees: '',
  paidFees: '0',
  pendingFees: '',
  placement: 'Yes',
  refName: '',
  refNumber: '',
  comments: '',
  status: 'active',
  joinedDate: '',
};

const StudentForm = ({
  initialData = initialForm,
  courseOptions = [],
  batchOptions = [],
  loading = false,
  toast,
  setToast,
  mode = "add",
  onSubmit,
  onCancel,
  onChangeCourse,
  initialAdditionalCourses = EMPTY_ADDITIONAL,
}) => {
  const [form, setForm] = useState(initialData);
  const [additionalCourses, setAdditionalCourses] = useState(initialAdditionalCourses);
  const [additionalBatchOptions, setAdditionalBatchOptions] = useState({});
  const [filteredBatchOptions, setFilteredBatchOptions] = useState(batchOptions);
  const [selectedBranch, setSelectedBranch] = useState('');
  const navigate = useNavigate();
  const { branches, isMultiBranch } = useBranch();

  useEffect(() => {
    setForm(initialData);
  }, [initialData]);

  // Keep filteredBatchOptions in sync with parent batchOptions when no branch selected
  useEffect(() => {
    if (!selectedBranch) {
      setFilteredBatchOptions(batchOptions);
    }
  }, [batchOptions, selectedBranch]);

  useEffect(() => {
    setAdditionalCourses(initialAdditionalCourses);
    initialAdditionalCourses.forEach((ac, idx) => {
      if (ac.courseId) fetchBatchesForAdditional(idx, ac.courseId);
    });
  }, [initialAdditionalCourses]);

  const fetchBatchesForAdditional = async (index, courseId) => {
    if (!courseId) {
      setAdditionalBatchOptions(prev => ({ ...prev, [index]: [] }));
      return;
    }
    try {
      const { data } = await apiClient.get("/batches/list", { params: { course: courseId } });
      setAdditionalBatchOptions(prev => ({
        ...prev,
        [index]: (data.batches || []).map(b => ({ value: b._id, label: b.name })),
      }));
    } catch {
      setAdditionalBatchOptions(prev => ({ ...prev, [index]: [] }));
    }
  };

  // Fetch batches filtered by branch for primary course
  const fetchBranchBatches = async (courseId, branchId) => {
    if (!courseId) { setFilteredBatchOptions([]); return; }
    try {
      const headers = branchId ? { "X-Branch-Id": branchId } : {};
      const { data } = await apiClient.get("/batches/list", { params: { course: courseId }, headers });
      setFilteredBatchOptions((data.batches || []).map(b => ({ value: b._id, label: b.name })));
    } catch {
      setFilteredBatchOptions([]);
    }
  };

  const handleBranchChange = (branchId) => {
    setSelectedBranch(branchId);
    setForm(prev => ({ ...prev, batch: '' }));
    if (form.course) {
      fetchBranchBatches(form.course, branchId);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let updatedForm = { ...form, [name]: value };
    if (name === "totalFees") {
      updatedForm.pendingFees = value;
    }
    if (name === "course") {
      updatedForm.batch = "";
      if (onChangeCourse) onChangeCourse(value);
      if (selectedBranch) {
        fetchBranchBatches(value, selectedBranch);
      }
    }
    setForm(updatedForm);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(form, additionalCourses);
  };

  const handleClear = () => {
    setForm(initialData);
    setAdditionalCourses([]);
    setAdditionalBatchOptions({});
  };

  const addAdditionalCourse = () => {
    setAdditionalCourses(prev => [...prev, { courseId: '', batchId: '', totalFees: '' }]);
  };

  const removeAdditionalCourse = (index) => {
    setAdditionalCourses(prev => prev.filter((_, i) => i !== index));
    setAdditionalBatchOptions(prev => {
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });
  };

  const updateAdditionalCourse = (index, field, value) => {
    setAdditionalCourses(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      if (field === 'courseId') {
        updated[index].batchId = '';
        // Auto-fill fees from the selected course
        const selectedCourse = courseOptions.find(c => c.value === value);
        updated[index].totalFees = selectedCourse?.fees !== undefined && selectedCourse.fees !== '' ? String(selectedCourse.fees) : '';
        fetchBatchesForAdditional(index, value);
      }
      return updated;
    });
  };

  return (
    <form className="sf-page" onSubmit={handleSubmit}>
      {/* ── Header ── */}
      <div className="sf-header">
        <div className="sf-header__left">
          <BackButton onClick={onCancel || (() => navigate("/students"))} />
          <div>
            <h1 className="sf-header__title">
              {mode === "edit" ? "Edit Student" : "Add New Student"}
            </h1>
            <p className="sf-header__subtitle">
              {mode === "edit" ? `Editing ${form.name || "student"}` : "Register a new student in your institute"}
            </p>
          </div>
        </div>
        <div className="sf-header__actions">
          {mode === "add" && (
            <AppButton variant="outline" type="button" onClick={handleClear}>
              Clear
            </AppButton>
          )}
          {onCancel && (
            <AppButton variant="outline" type="button" onClick={onCancel}>
              Cancel
            </AppButton>
          )}
          <AppButton variant="default" type="submit" disabled={loading}>
            <Save size={16} />
            {loading ? (mode === "edit" ? "Saving…" : "Adding…") : (mode === "edit" ? "Save Changes" : "Add Student")}
          </AppButton>
        </div>
      </div>

      {/* ── Form Body ── */}
      <div className="sf-body">
        <div className="sf-columns">
          {/* ═══ Left Column ═══ */}
          <div className="sf-col">
            {/* ── Personal Details ── */}
            <AppCard className="sf-card">
              <AppCardHeader>
                <AppCardTitle className="sf-section-title">
                  <User size={16} /> Personal Details
                </AppCardTitle>
              </AppCardHeader>
              <AppCardContent>
                <div className="sf-grid">
                  <div className="sf-field">
                    <label className="sf-label">Full Name <span className="sf-req">*</span></label>
                    <input className="sf-input" name="name" value={form.name} onChange={handleChange} placeholder="Enter full name" required />
                  </div>
                  <div className="sf-field">
                    <label className="sf-label">Mobile Number <span className="sf-req">*</span></label>
                    <input className="sf-input" name="mobile" type="tel" pattern="[0-9]{10}" value={form.mobile} onChange={handleChange} placeholder="10-digit number" required />
                  </div>
                  <div className="sf-field">
                    <label className="sf-label">Email <span className="sf-req">*</span></label>
                    <input className="sf-input" name="email" type="email" value={form.email} onChange={handleChange} placeholder="student@email.com" required />
                  </div>
                  <div className="sf-field">
                    <label className="sf-label">Date of Birth</label>
                    <input className="sf-input" type="date" name="dob" value={form.dob ? form.dob.slice(0, 10) : ""} onChange={handleChange} />
                  </div>
                  <div className="sf-field">
                    <label className="sf-label">Gender</label>
                    <select className="sf-select" name="gender" value={form.gender} onChange={handleChange}>
                      <option value="">Select gender</option>
                      <option>Male</option>
                      <option>Female</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <div className="sf-field">
                    <label className="sf-label">Address</label>
                    <input className="sf-input" name="address" value={form.address} onChange={handleChange} placeholder="City, State" />
                  </div>
                </div>
              </AppCardContent>
            </AppCard>

            {/* ── Course Details ── */}
            <AppCard className="sf-card">
              <AppCardHeader>
                <AppCardTitle className="sf-section-title">
                  <BookOpen size={16} /> Course Details
                </AppCardTitle>
              </AppCardHeader>
              <AppCardContent>
                <div className="sf-grid">
                  {isMultiBranch && branches.length > 1 && (
                    <div className="sf-field">
                      <label className="sf-label"><Building2 size={14} className="me-1" />Branch</label>
                      <select className="sf-select" value={selectedBranch} onChange={(e) => handleBranchChange(e.target.value)}>
                        <option value="">All Branches</option>
                        {branches.filter(b => b.isActive).map(b => (
                          <option key={b._id} value={b._id}>{b.name}{b.isDefault ? ' (Default)' : ''}</option>
                        ))}
                      </select>
                      <span className="sf-hint">Filter batches by branch</span>
                    </div>
                  )}
                  <div className="sf-field">
                    <label className="sf-label">Course</label>
                    <select className="sf-select" name="course" value={form.course} onChange={handleChange}>
                      <option value="">Select course</option>
                      {courseOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    <span className="sf-hint">You can assign course and batch later.</span>
                  </div>
                  <div className="sf-field">
                    <label className="sf-label">Batch</label>
                    <select className="sf-select" name="batch" value={form.batch} onChange={handleChange} disabled={!form.course}>
                      <option value="">Select batch</option>
                      {filteredBatchOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="sf-field">
                    <label className="sf-label">Degree Type</label>
                    <select className="sf-select" name="degree" value={form.degree} onChange={handleChange}>
                      <option value="">Select degree</option>
                      <option>B.Tech</option>
                      <option>M.Tech</option>
                      <option>B.Sc</option>
                      <option>M.Sc</option>
                    </select>
                  </div>
                  <div className="sf-field">
                    <label className="sf-label">Passed Out Year</label>
                    <input className="sf-input" name="passout" value={form.passout} onChange={handleChange} placeholder="e.g. 2024" />
                  </div>
                  <div className="sf-field">
                    <label className="sf-label">Experience (years)</label>
                    <input className="sf-input" name="experience" value={form.experience} onChange={handleChange} placeholder="e.g. 2" />
                  </div>
                  <div className="sf-field">
                    <label className="sf-label">Joined Date</label>
                    <input className="sf-input" type="date" name="joinedDate" value={form.joinedDate ? form.joinedDate.slice(0, 10) : ""} onChange={handleChange} />
                  </div>
                </div>
              </AppCardContent>
            </AppCard>

            {/* ── Additional Courses ── */}
            <AppCard className="sf-card">
              <AppCardHeader>
                <div className="sf-section-header-row">
                  <AppCardTitle className="sf-section-title">
                    <GraduationCap size={16} /> Additional Courses
                  </AppCardTitle>
                  <AppButton variant="outline" size="sm" type="button" onClick={addAdditionalCourse}>
                    <Plus size={14} /> Add Course
                  </AppButton>
                </div>
              </AppCardHeader>
              <AppCardContent>
                {additionalCourses.length === 0 ? (
                  <p className="sf-empty-text">No additional courses. Click "Add Course" to assign more courses to this student.</p>
                ) : (
                  <div className="sf-additional-list">
                    {additionalCourses.map((ac, idx) => (
                      <div key={idx} className="sf-additional-row">
                        <div className="sf-additional-row__fields">
                          <div className="sf-field">
                            <label className="sf-label sf-label--sm">Course</label>
                            <select className="sf-select" value={ac.courseId} onChange={(e) => updateAdditionalCourse(idx, 'courseId', e.target.value)}>
                              <option value="">Select course</option>
                              {courseOptions.filter(opt => opt.value !== form.course).map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          </div>
                          <div className="sf-field">
                            <label className="sf-label sf-label--sm">Batch</label>
                            <select className="sf-select" value={ac.batchId} onChange={(e) => updateAdditionalCourse(idx, 'batchId', e.target.value)} disabled={!ac.courseId}>
                              <option value="">Select batch</option>
                              {(additionalBatchOptions[idx] || []).map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          </div>
                          <div className="sf-field">
                            <label className="sf-label sf-label--sm">Fees</label>
                            <input className="sf-input" type="number" value={ac.totalFees} onChange={(e) => updateAdditionalCourse(idx, 'totalFees', e.target.value)} min={0} placeholder="0" />
                          </div>
                        </div>
                        <AppButton variant="destructive" size="sm" type="button" onClick={() => removeAdditionalCourse(idx)}>
                          <Trash2 size={14} />
                        </AppButton>
                      </div>
                    ))}
                  </div>
                )}
              </AppCardContent>
            </AppCard>
          </div>

          {/* ═══ Right Column ═══ */}
          <div className="sf-col">
            {/* ── Fees Details ── */}
            <AppCard className="sf-card">
              <AppCardHeader>
                <AppCardTitle className="sf-section-title">
                  <IndianRupee size={16} /> Fees Details
                  <span className="sf-label-hint">(Can't collect fee here)</span>
                </AppCardTitle>
              </AppCardHeader>
              <AppCardContent>
                <div className="sf-grid">
                  <div className="sf-field">
                    <label className="sf-label">Total Fees</label>
                    <input className="sf-input" type="number" name="totalFees" value={form.totalFees} onChange={handleChange} min={0} placeholder="₹ 0" />
                  </div>
                  <div className="sf-field">
                    <label className="sf-label">Paid Fees</label>
                    <input className="sf-input sf-input--readonly" type="number" name="paidFees" value={form.paidFees || '0'} readOnly />
                  </div>
                  <div className="sf-field">
                    <label className="sf-label">Pending Fees</label>
                    <input className="sf-input sf-input--readonly" type="number" name="pendingFees" value={form.pendingFees} readOnly />
                  </div>
                </div>
              </AppCardContent>
            </AppCard>

            {/* ── Other Details ── */}
            <AppCard className="sf-card">
              <AppCardHeader>
                <AppCardTitle className="sf-section-title">
                  <ClipboardList size={16} /> Other Details
                </AppCardTitle>
              </AppCardHeader>
              <AppCardContent>
                <div className="sf-grid">
                  <div className="sf-field">
                    <label className="sf-label">Placement Required?</label>
                    <div className="sf-radio-group">
                      <label className={`sf-radio-card ${form.placement === "Yes" ? "sf-radio-card--active" : ""}`}>
                        <input type="radio" name="placement" value="Yes" checked={form.placement === "Yes"} onChange={handleChange} />
                        <span className="sf-radio-card__label">Yes</span>
                      </label>
                      <label className={`sf-radio-card ${form.placement === "No" ? "sf-radio-card--active" : ""}`}>
                        <input type="radio" name="placement" value="No" checked={form.placement === "No"} onChange={handleChange} />
                        <span className="sf-radio-card__label">No</span>
                      </label>
                    </div>
                  </div>
                  <div className="sf-field">
                    <label className="sf-label">Status</label>
                    <select className="sf-select" name="status" value={form.status} onChange={handleChange}>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                  <div className="sf-field">
                    <label className="sf-label">Reference Name</label>
                    <input className="sf-input" name="refName" value={form.refName} onChange={handleChange} placeholder="Who referred this student?" />
                  </div>
                  <div className="sf-field">
                    <label className="sf-label">Reference Number</label>
                    <input className="sf-input" name="refNumber" value={form.refNumber} onChange={handleChange} placeholder="Reference contact" />
                  </div>
                  <div className="sf-field sf-field--full">
                    <label className="sf-label">Comments</label>
                    <textarea className="sf-textarea" name="comments" value={form.comments} onChange={handleChange} rows={3} placeholder="Any additional notes about this student…" />
                  </div>
                </div>
              </AppCardContent>
            </AppCard>
          </div>
        </div>
      </div>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </form>
  );
};

export default StudentForm;
